import { Box, Stack, Text, TextLink, Skeleton, FieldMessage } from '@machobear/design-system'
import { Trans, useTranslation } from 'react-i18next'
import { isEarlierThanCurrentMMYY } from '@/utils/check-expiry'
import HasPaymentMethodCard from './has-payment-method-card'
import PaymentForm from './payment-form'
import { PaymentMethodType } from './types'
import { ShieldIcon } from '../icons'

type Props = {
  isLoading: boolean
  paymentInfo: PaymentMethodType[]
}

const PaymentMethod = ({ isLoading, paymentInfo = [] }: Props) => {
  const { t } = useTranslation()
  const hasPaymentMethods = paymentInfo?.length !== 0
  const showExpiredWarnings = hasPaymentMethods
    ? isEarlierThanCurrentMMYY(paymentInfo ? paymentInfo[0]?.expiration_date : '')
    : false

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="spaceBetween"
      gap="medium"
      padding="large"
    >
      <Box display="flex" justifyContent="spaceBetween" alignItems="center">
        <Text size="large" weight="strong">
          {t('b.checkout.title.payment_method', 'Payment Method')}
        </Text>
        <Box display="flex" alignItems="center" gap="xxxsmall">
          <ShieldIcon width={16} height={16} />
          <Text tone="tertiary" size="xsmall">
            {t('b.checkout.title.secure_ecryption', 'Secure Encryption')}
          </Text>
        </Box>
      </Box>

      {isLoading ? (
        <Skeleton.Shape height={96} width={'100%'} />
      ) : (
        <Stack space="xsmall">
          {hasPaymentMethods ? (
            paymentInfo?.map((paymentMethod: PaymentMethodType) => (
              <HasPaymentMethodCard
                key={paymentMethod.payment_method_id}
                paymentMethod={paymentMethod}
              />
            ))
          ) : (
            <PaymentForm />
          )}
          {showExpiredWarnings && (
            <FieldMessage
              tone="critical"
              message="Please update your payment method."
              id="expiration warning"
            />
          )}
        </Stack>
      )}

      <Trans i18nKey="l.checkout.modal.purchase_terms.text3">
        <Text size="xsmall" tone="secondary">
          By clicking “Submit Purchase”, I agree to a one-time charge and confirm that I have read
          and agree to liv.rent's{' '}
          <TextLink href="https://liv.rent/privacy" target="_blank">
            Privacy Policy
          </TextLink>{' '}
          and{' '}
          <TextLink href="https://liv.rent/terms" target="_blank">
            Terms & Conditions
          </TextLink>
          .
        </Text>
      </Trans>
    </Box>
  )
}

export default PaymentMethod
